import { cspManager } from './cspManager';
import { threatMonitor } from './threatMonitor';

export interface SecurityHeadersConfiguration {
  enabled: boolean;
  hsts: {
    enabled: boolean;
    maxAge: number;
    includeSubDomains: boolean;
    preload: boolean;
  };
  xFrameOptions: {
    enabled: boolean;
    value: 'DENY' | 'SAMEORIGIN' | string;
  };
  xContentTypeOptions: {
    enabled: boolean;
    value: 'nosniff';
  };
  referrerPolicy: {
    enabled: boolean;
    value: 'strict-origin-when-cross-origin' | 'no-referrer' | 'same-origin' | string;
  };
  permissionsPolicy: {
    enabled: boolean;
    directives: Record<string, string[]>;
  };
  crossOriginEmbedderPolicy: {
    enabled: boolean;
    value: 'require-corp' | 'unsafe-none';
  };
  crossOriginOpenerPolicy: {
    enabled: boolean;
    value: 'same-origin' | 'same-origin-allow-popups' | 'unsafe-none';
  };
  crossOriginResourcePolicy: {
    enabled: boolean;
    value: 'same-origin' | 'same-site' | 'cross-origin';
  };
  xXSSProtection: {
    enabled: boolean;
    value: '1; mode=block' | '0';
  };
  expectCT: {
    enabled: boolean;
    maxAge: number;
    enforce: boolean;
    reportUri?: string;
  };
  customHeaders: Record<string, string>;
}

export interface SecurityHeadersResult {
  headers: Record<string, string>;
  warnings: string[];
  errors: string[];
}

class SecurityHeaders {
  private configuration: SecurityHeadersConfiguration;

  constructor() {
    this.configuration = this.getDefaultConfiguration();
  }

  /**
   * Generate all security headers
   */
  generateHeaders(request?: { 
    secure?: boolean; 
    host?: string; 
    userAgent?: string; 
    ip?: string; 
  }): SecurityHeadersResult {
    const result: SecurityHeadersResult = {
      headers: {},
      warnings: [],
      errors: []
    };

    if (!this.configuration.enabled) {
      result.warnings.push('Security headers are disabled');
      return result;
    }

    try {
      // Content Security Policy
      const cspHeaderName = cspManager.getCSPHeaderName();
      const cspHeaderValue = cspManager.generateCSPHeader();
      result.headers[cspHeaderName] = cspHeaderValue;

      // Strict Transport Security
      if (this.configuration.hsts.enabled) {
        if (request?.secure) {
          let hstsValue = `max-age=${this.configuration.hsts.maxAge}`;
          if (this.configuration.hsts.includeSubDomains) {
            hstsValue += '; includeSubDomains';
          }
          if (this.configuration.hsts.preload) {
            hstsValue += '; preload';
          }
          result.headers['Strict-Transport-Security'] = hstsValue;
        } else {
          result.warnings.push('HSTS header skipped for non-secure request');
        }
      }

      // X-Frame-Options
      if (this.configuration.xFrameOptions.enabled) {
        result.headers['X-Frame-Options'] = this.configuration.xFrameOptions.value;
      }

      // X-Content-Type-Options
      if (this.configuration.xContentTypeOptions.enabled) {
        result.headers['X-Content-Type-Options'] = this.configuration.xContentTypeOptions.value;
      }

      // Referrer Policy
      if (this.configuration.referrerPolicy.enabled) {
        result.headers['Referrer-Policy'] = this.configuration.referrerPolicy.value;
      }

      // Permissions Policy
      if (this.configuration.permissionsPolicy.enabled) {
        const permissionsPolicy = this.buildPermissionsPolicy();
        if (permissionsPolicy) {
          result.headers['Permissions-Policy'] = permissionsPolicy;
        }
      }

      // Cross-Origin Embedder Policy
      if (this.configuration.crossOriginEmbedderPolicy.enabled) {
        result.headers['Cross-Origin-Embedder-Policy'] = this.configuration.crossOriginEmbedderPolicy.value;
      }

      // Cross-Origin Opener Policy
      if (this.configuration.crossOriginOpenerPolicy.enabled) {
        result.headers['Cross-Origin-Opener-Policy'] = this.configuration.crossOriginOpenerPolicy.value;
      }

      // Cross-Origin Resource Policy
      if (this.configuration.crossOriginResourcePolicy.enabled) {
        result.headers['Cross-Origin-Resource-Policy'] = this.configuration.crossOriginResourcePolicy.value;
      }

      // X-XSS-Protection (deprecated but still useful for older browsers)
      if (this.configuration.xXSSProtection.enabled) {
        result.headers['X-XSS-Protection'] = this.configuration.xXSSProtection.value;
      }

      // Expect-CT
      if (this.configuration.expectCT.enabled && request?.secure) {
        let expectCTValue = `max-age=${this.configuration.expectCT.maxAge}`;
        if (this.configuration.expectCT.enforce) {
          expectCTValue += ', enforce';
        }
        if (this.configuration.expectCT.reportUri) {
          expectCTValue += `, report-uri="${this.configuration.expectCT.reportUri}"`;
        }
        result.headers['Expect-CT'] = expectCTValue;
      }

      // Custom headers
      Object.entries(this.configuration.customHeaders).forEach(([name, value]) => {
        result.headers[name] = value;
      });

      // Log security header generation
      threatMonitor.logEvent({
        type: 'system_error',
        severity: 'low',
        source: 'security_headers',
        ipAddress: request?.ip,
        userAgent: request?.userAgent,
        details: {
          action: 'headers_generated',
          headerCount: Object.keys(result.headers).length,
          warnings: result.warnings.length,
          secure: request?.secure || false,
          host: request?.host
        }
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      result.errors.push(`Failed to generate security headers: ${errorMessage}`);
      
      threatMonitor.logEvent({
        type: 'system_error',
        severity: 'high',
        source: 'security_headers',
        details: {
          action: 'header_generation_failed',
          error: errorMessage
        }
      });
    }

    return result;
  }

  /**
   * Validate request headers for security issues
   */
  validateRequestHeaders(headers: Record<string, string>): {
    isValid: boolean;
    issues: Array<{ severity: 'low' | 'medium' | 'high'; message: string; }>;
  } {
    const issues: Array<{ severity: 'low' | 'medium' | 'high'; message: string; }> = [];

    // Check for potentially dangerous headers
    const dangerousHeaders = [
      'x-forwarded-for',
      'x-real-ip',
      'x-cluster-client-ip',
      'cf-connecting-ip'
    ];

    dangerousHeaders.forEach(header => {
      if (headers[header]) {
        issues.push({
          severity: 'medium',
          message: `Potentially spoofed header detected: ${header}`
        });
      }
    });

    // Check for suspicious user agents
    const userAgent = headers['user-agent'];
    if (userAgent) {
      const suspiciousPatterns = [
        /bot/i,
        /crawler/i,
        /spider/i,
        /scraper/i,
        /curl/i,
        /wget/i,
        /python/i,
        /java/i
      ];

      const isSuspicious = suspiciousPatterns.some(pattern => pattern.test(userAgent));
      if (isSuspicious) {
        issues.push({
          severity: 'low',
          message: 'Suspicious user agent detected'
        });
      }
    }

    // Check for missing expected headers
    if (!headers['accept']) {
      issues.push({
        severity: 'low',
        message: 'Missing Accept header'
      });
    }

    if (!headers['accept-language']) {
      issues.push({
        severity: 'low',
        message: 'Missing Accept-Language header'
      });
    }

    // Check for overly long headers
    Object.entries(headers).forEach(([name, value]) => {
      if (value.length > 8192) {
        issues.push({
          severity: 'high',
          message: `Abnormally long header: ${name}`
        });
      }
    });

    return {
      isValid: issues.filter(issue => issue.severity === 'high').length === 0,
      issues
    };
  }

  /**
   * Update security headers configuration
   */
  updateConfiguration(config: Partial<SecurityHeadersConfiguration>): void {
    this.configuration = { ...this.configuration, ...config };
    
    threatMonitor.logEvent({
      type: 'system_error',
      severity: 'low',
      source: 'security_headers',
      details: {
        action: 'configuration_updated',
        changes: Object.keys(config)
      }
    });
  }

  /**
   * Get current configuration
   */
  getConfiguration(): SecurityHeadersConfiguration {
    return { ...this.configuration };
  }

  /**
   * Get security headers statistics
   */
  getStatistics(): {
    enabledHeaders: string[];
    configuredPolicies: number;
    customHeaders: number;
  } {
    const enabledHeaders: string[] = [];
    let configuredPolicies = 0;

    if (this.configuration.hsts.enabled) {
      enabledHeaders.push('Strict-Transport-Security');
      configuredPolicies++;
    }

    if (this.configuration.xFrameOptions.enabled) {
      enabledHeaders.push('X-Frame-Options');
      configuredPolicies++;
    }

    if (this.configuration.xContentTypeOptions.enabled) {
      enabledHeaders.push('X-Content-Type-Options');
      configuredPolicies++;
    }

    if (this.configuration.referrerPolicy.enabled) {
      enabledHeaders.push('Referrer-Policy');
      configuredPolicies++;
    }

    if (this.configuration.permissionsPolicy.enabled) {
      enabledHeaders.push('Permissions-Policy');
      configuredPolicies++;
    }

    if (this.configuration.crossOriginEmbedderPolicy.enabled) {
      enabledHeaders.push('Cross-Origin-Embedder-Policy');
      configuredPolicies++;
    }

    if (this.configuration.crossOriginOpenerPolicy.enabled) {
      enabledHeaders.push('Cross-Origin-Opener-Policy');
      configuredPolicies++;
    }

    if (this.configuration.crossOriginResourcePolicy.enabled) {
      enabledHeaders.push('Cross-Origin-Resource-Policy');
      configuredPolicies++;
    }

    return {
      enabledHeaders,
      configuredPolicies,
      customHeaders: Object.keys(this.configuration.customHeaders).length
    };
  }

  /**
   * Build Permissions Policy header value
   */
  private buildPermissionsPolicy(): string {
    const directives: string[] = [];

    Object.entries(this.configuration.permissionsPolicy.directives).forEach(([directive, allowlist]) => {
      if (allowlist.length === 0) {
        directives.push(`${directive}=()`);
      } else {
        const formattedAllowlist = allowlist.map(origin => {
          if (origin === 'self') return '"self"';
          if (origin === '*') return '*';
          return `"${origin}"`;
        }).join(' ');
        directives.push(`${directive}=(${formattedAllowlist})`);
      }
    });

    return directives.join(', ');
  }

  /**
   * Get default security headers configuration
   */
  private getDefaultConfiguration(): SecurityHeadersConfiguration {
    const isProduction = process.env.NODE_ENV === 'production';

    return {
      enabled: true,
      hsts: {
        enabled: isProduction,
        maxAge: 31536000, // 1 year
        includeSubDomains: true,
        preload: isProduction
      },
      xFrameOptions: {
        enabled: true,
        value: 'DENY'
      },
      xContentTypeOptions: {
        enabled: true,
        value: 'nosniff'
      },
      referrerPolicy: {
        enabled: true,
        value: 'strict-origin-when-cross-origin'
      },
      permissionsPolicy: {
        enabled: true,
        directives: {
          camera: [],
          microphone: [],
          geolocation: [],
          payment: [],
          usb: [],
          bluetooth: [],
          accelerometer: [],
          gyroscope: [],
          magnetometer: [],
          fullscreen: ['self'],
          'picture-in-picture': [],
          'display-capture': []
        }
      },
      crossOriginEmbedderPolicy: {
        enabled: false, // Can break external resources
        value: 'unsafe-none'
      },
      crossOriginOpenerPolicy: {
        enabled: true,
        value: 'same-origin'
      },
      crossOriginResourcePolicy: {
        enabled: true,
        value: 'same-origin'
      },
      xXSSProtection: {
        enabled: true,
        value: '1; mode=block'
      },
      expectCT: {
        enabled: isProduction,
        maxAge: 86400, // 24 hours
        enforce: false // Start with report-only
      },
      customHeaders: {
        'X-Robots-Tag': 'noindex, nofollow, nosnippet, noarchive',
        'Cache-Control': 'no-store, no-cache, must-revalidate, private',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    };
  }
}

// Export singleton instance
export const securityHeaders = new SecurityHeaders();
export default securityHeaders;